import PaymentScheduleTable from '@acorn-portal/shared/src/components/display/payment-schedule-table';
import DisplayField from '@acorn-portal/shared/src/components/displayField';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import { PolicyCancelledWithDebt, PolicyInactive } from '@acorn-portal/shared/src/components/paymentDetails';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useManagedPaymentsDetails from '@acorn-portal/shared/src/models/managedPayments-model';
import usePayNowDetails from '@acorn-portal/shared/src/models/paynow-model';
import { usePolicy } from '@acorn-portal/shared/src/models/policy-model';
import DirectDebitFields from '@acorn-portal/shared/src/pages/payNow/components/DirectDebitFields';
import {
	GetPaymentDetailsResponseModel,
	PayNowClient,
	PayNowStatus,
	PolicyStatus,
} from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { IPayNow, updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import { addOrdinalSuffix } from '@acorn-portal/shared/src/utils';
import { DISPLAY_DATE } from '@acorn-portal/shared/src/utils/constants';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import SortDrivers from '@acorn-portal/shared/src/utils/sortDrivers';
import { Edit, Home } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Container, Fade, Grid, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isBillingBankAccountPaymentMethod } from '../../utils/payment-method-type-guard';

import PayNowAlerts from '@acorn-portal/shared/src/components/content/pay-now-alerts';
import {
	BillingTermSummaryResponse,
	GetPaymentDayResponse,
} from '@acorn-portal/shared/src/services/managedPaymentsApi';

const checkPolicyConditions = (
	isExpired: boolean,
	isDirectDebit: boolean,
	details: GetPaymentDetailsResponseModel | undefined,
) => {
	if (isExpired) {
		if (details?.totalBalanceOutstanding && details.totalBalanceOutstanding > 0) {
			return <PolicyCancelledWithDebt />;
		} else {
			return <PolicyInactive />;
		}
	}

	if (isDirectDebit && details) return <DirectDebitFields details={details} />;

	return null;
};

function checkConditions(
	details: GetPaymentDetailsResponseModel | undefined,
	paymentDay: GetPaymentDayResponse,
	currentBillingTerm: BillingTermSummaryResponse,
	payNowDetails: IPayNow,
) {
	if (payNowDetails.status === 'failed') return false;

	if (!details) return true;

	const detailsCondition =
		details.missedPaymentDetails === null &&
		details.splitPaymentPlans.length <= 0 &&
		details.totalBalanceOutstanding != 0;

	if (detailsCondition) {
		const loadingCondition =
			paymentDay?.paymentDay === undefined &&
			currentBillingTerm?.currentPaymentMethod?.settlementMethodCode === undefined;
		return loadingCondition;
	}

	return !(!detailsCondition && details);
}

const PaymentAgreement: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { policyToken } = useParams();
	const isChangeOfPaymentDateEnabled = useFeatureFlagEnabled('change_payment_day');
	const { fullPolicy } = usePolicy(policyToken as string);
	const policyDetails = useAppSelector((redux) => redux.fullPolicy);
	const { payNowDetails } = usePayNowDetails(policyDetails.policyNumber);
	const { managedPaymentsDetails } = useManagedPaymentsDetails(policyDetails.policyToken ?? fullPolicy.policyToken);
	const { details } = payNowDetails;

	const { currentBillingTerm, paymentDay, paymentSchedule } = managedPaymentsDetails;
	const { drivers } = fullPolicy;
	const { policyHolder } = SortDrivers(drivers);

	const isConsolidatedPaymentSchedule = payNowDetails.details?.status === PayNowStatus.ConsolidatedPaymentSchedule;

	const isExpired =
		policyDetails.policyStatus === PolicyStatus.Cancelled || payNowDetails.policyExceptionReason === 'POLICY_EXPIRED';

	useEffect(() => {
		const getPaymentCollectionOptions = async () => {
			try {
				if (
					payNowDetails.details?.missedPaymentDetails?.directDebitId &&
					payNowDetails.details?.missedPaymentDetails?.directDebitId !== ''
				) {
					const client = new PayNowClient(undefined, API);
					const result = await client.getPaymentCollectionOptions({
						defaultedDirectDebitId: payNowDetails.details?.missedPaymentDetails?.directDebitId,
					});
					dispatch(updatePayNowDetails({ collectionOptions: result }));
				}
			} catch (error) {
				console.error('Error fetching payment collection options:', error);
				navigate(ROUTE_PATHS.error);
			}
		};

		if (payNowDetails.details && payNowDetails.details.status === PayNowStatus.SplitPaymentOverdue) {
			navigate(ROUTE_PATHS.paymentPlanOverdue, { replace: true });
			return;
		}

		if (payNowDetails.collectionOptions.length === 0) {
			getPaymentCollectionOptions().catch(console.error);
		}
	}, [dispatch]);

	let sortCode: string | undefined;
	let accountNumber: string | undefined;

	if (
		currentBillingTerm.currentPaymentMethod &&
		isBillingBankAccountPaymentMethod(currentBillingTerm.currentPaymentMethod)
	) {
		sortCode = currentBillingTerm.currentPaymentMethod.sortCode;
		accountNumber = currentBillingTerm.currentPaymentMethod.accountNumber;
	}

	return (
		<FullPageLoadingSplash
			loading={checkConditions(details, paymentDay, currentBillingTerm, payNowDetails)}
			message='Retrieving Balance Details...'
		>
			<Paper
				elevation={1}
				sx={{
					margin: 1,
					borderRadius: '8px',
					paddingBottom: 3,
				}}
			>
				<Fade in>
					<Container maxWidth='md' sx={{ padding: 3 }}>
						<PageTitle>Payment Details</PageTitle>

						<Box
							sx={(theme) => ({
								maxWidth: '100%',
								margin: 'auto',
								display: 'flex',
								flexDirection: 'column',
								[theme.breakpoints.down('md')]: {
									maxWidth: '100%',
								},
							})}
						>
							<PayNowAlerts payNowDetails={payNowDetails.details} />
							<Box my={2}>
								<Grid container margin={0}>
									<Box
										sx={(theme) => ({
											border: `1px solid ${theme.palette.divider}`,
											p: 2,
											width: '100%',
											my: 1,
											borderRadius: 2,
										})}
									>
										<Typography variant='h6' color='secondary' sx={{ mb: 2 }}>
											Policy Details
										</Typography>
										<Grid size={{ xs: 10, md: 12 }}>
											<DisplayField label='Policy Holder'>
												{details?.policyHolderCompanyName ||
													`${details?.policyHolderFirstName || policyHolder.firstName} ${details?.policyHolderLastName || policyHolder.lastName}`}
											</DisplayField>
										</Grid>

										<Grid size={{ xs: 10, md: 12 }}>
											<DisplayField label='Policy Number'>
												{details?.policyReference ?? policyDetails.policyNumber}
											</DisplayField>
										</Grid>
									</Box>
									{policyDetails.isDirectDebitPaymentMethod &&
										currentBillingTerm.currentPaymentMethod &&
										!isExpired && (
											<Box
												sx={(theme) => ({
													border: `1px solid ${theme.palette.divider}`,
													p: 2,
													width: '100%',
													my: 1,
													borderRadius: 2,
												})}
											>
												<Box display='flex'>
													<Typography variant='h6' color='secondary' sx={{ mb: 2 }}>
														Bank Details
													</Typography>

													{paymentSchedule &&
														paymentSchedule.canChangeBankDetails === true &&
														!isConsolidatedPaymentSchedule && (
															<Button
																sx={{
																	alignSelf: 'baseline',
																	justifyContent: 'flex-end',
																	marginLeft: 'auto',
																	':hover': { backgroundColor: 'unset' },
																}}
																startIcon={<Edit />}
																onClick={() => navigate(ROUTE_PATHS.paymentManagementChangeBankDetails)}
															>
																Change
															</Button>
														)}
												</Box>
												<Grid size={{ xs: 10, md: 12 }}>
													<DisplayField label='Account Number'>{accountNumber}</DisplayField>
												</Grid>
												<Grid size={{ xs: 10, md: 12 }}>
													<DisplayField label='Sort Code'>{sortCode}</DisplayField>
												</Grid>
											</Box>
										)}
									{paymentDay.paymentDay !== undefined && policyDetails.isDirectDebitPaymentMethod && !isExpired && (
										<Box
											sx={(theme) => ({
												border: `1px solid ${theme.palette.divider}`,
												p: 2,
												width: '100%',
												my: 1,
												borderRadius: 2,
											})}
										>
											<Box display='flex'>
												<Typography variant='h6' color='secondary' sx={{ mb: 2 }}>
													Payment Schedule
												</Typography>
												{isChangeOfPaymentDateEnabled && paymentDay.canChangePaymentDay && (
													<Button
														sx={{
															alignSelf: 'baseline',
															justifyContent: 'flex-end',
															marginLeft: 'auto',
															':hover': { backgroundColor: 'unset' },
														}}
														startIcon={<Edit />}
														onClick={() => navigate(ROUTE_PATHS.paymentManagementChangePaymentDate)}
													>
														Change
													</Button>
												)}
											</Box>
											<Grid size={{ xs: 10, md: 12 }}>
												<Box sx={{ display: 'flex', flexDirection: 'row' }}>
													<DisplayField label='Preferred Payment Day'>
														{`${addOrdinalSuffix(paymentDay.paymentDay)} of each month`}
													</DisplayField>
												</Box>
											</Grid>
											{details?.nextDirectDebitDue && (
												<Grid size={{ xs: 10, md: 12 }}>
													<DisplayField label='Direct Debit payment due'>
														{format(new Date(details?.nextDirectDebitDue), DISPLAY_DATE)}
													</DisplayField>
												</Grid>
											)}
											<Box textAlign='center'>
												<Button
													sx={{ ':hover': { backgroundColor: 'unset' } }}
													onClick={() => navigate(ROUTE_PATHS.fullPaymentSchedule)}
													startIcon={<VisibilityIcon />}
												>
													View Full Payment Schedule
												</Button>
											</Box>
										</Box>
									)}
									{checkPolicyConditions(isExpired, policyDetails.isDirectDebitPaymentMethod, details)}
								</Grid>
							</Box>
							{details?.splitPaymentPlans && details.splitPaymentPlans.length > 0 && !isConsolidatedPaymentSchedule ? (
								<>
									<Box
										sx={(theme) => ({
											border: `1px solid ${theme.palette.divider}`,
											p: 2,
											width: '100%',
											mb: 2,
											borderRadius: 2,
										})}
									>
										<Typography variant='h6' component='h6' gutterBottom sx={{ textAlign: 'center' }}>
											Missed Direct Debit Installment plan
										</Typography>
										<PaymentScheduleTable splitPaymentPlans={details.splitPaymentPlans} />
									</Box>
								</>
							) : null}

							<Button
								variant='contained'
								color='primary'
								onClick={() => navigate(ROUTE_PATHS.home)}
								startIcon={<Home />}
								size='large'
								fullWidth
								sx={(theme) => ({
									maxWidth: '60%',
									margin: 'auto',
									[theme.breakpoints.down('md')]: {
										maxWidth: '100%',
									},
								})}
							>
								Go Home
							</Button>
						</Box>
					</Container>
				</Fade>
			</Paper>
		</FullPageLoadingSplash>
	);
};

export default PaymentAgreement;
